import { setAuthToken as setSharedApiAuthToken } from '@ping/ui/src/network/api';

import { log } from '@core/log/log';
import { clearIdentity } from '@core/persistence/identity';
import {
  clearRefreshToken,
  clearStoredAccessToken,
  retrieveRefreshToken,
  storeWrappedAccessToken,
  storeWrappedRefreshToken,
} from '@core/persistence/tokens';

import { refreshAccessToken } from './authClient';
import { NetworkError, type RefreshResponse } from './types';

let inMemoryAccessToken: string | null = null;

// Epoch-ms at which the in-memory access token expires (derived from the
// BE's `expires_in`). `null` when unknown — in that case we never
// proactively refresh and fall back to the reactive 401 path.
let accessTokenExpiresAt: number | null = null;

let inFlightRefresh: Promise<RefreshResponse> | null = null;

// Last successful refresh result + when it landed. Lets near-simultaneous
// callers (React StrictMode double-invokes the silent-reauth effect; a WS
// reconnect + a foreground 401 racing) REUSE a just-minted token instead
// of each POSTing the SINGLE-USE rotating refresh token — the second POST
// would hit a now-consumed token and 401, logging the user out on a dev
// restart. The `inFlightRefresh` dedupe only collapses *overlapping*
// calls; this window also covers *sequential* calls a few ms apart.
let lastRefreshResult: RefreshResponse | null = null;
let lastRefreshAtMs = 0;
// Must exceed the WS reconnect MAX_BACKOFF (socket.ts, 30s) so two reconnect
// ticks that each call performRefresh reuse the just-minted result instead of
// both POSTing the single-use rotating token (the 2nd would 401 → logout). The
// BE now also has a 60s refresh grace window; this is the client-side belt to
// that suspenders.
const REFRESH_REUSE_WINDOW_MS = 35_000;

// App-level "this session is dead, return to welcome" callback, registered
// by `installRefreshInterceptor`. Hoisted to module scope so the WS
// reconnect paths (which call `performRefresh` directly, NOT through a
// foreground fetch) can trigger the SAME terminal teardown the reactive
// 401 path does — see `handleTerminalAuthFailure`.
let reauthCallback: (() => void) | null = null;

// One-shot guard so a burst of terminal failures (the WS reconnect storm
// firing `performRefresh` every backoff tick, racing a foreground 401)
// only tears the session down ONCE. Reset on the next successful auth
// (`setAccessToken`) so a fresh sign-in re-arms it.
let terminalAuthHandled = false;

/**
 * True when we still hold a USABLE access token — present in memory and not
 * past its known expiry. The access token (year-long) is the live credential
 * for every API + WS call; an unknown expiry (null) is treated as usable
 * because the BE issues long-lived tokens and an in-memory token is almost
 * certainly still valid.
 */
function accessTokenIsUsable(): boolean {
  if (inMemoryAccessToken === null) return false;
  if (accessTokenExpiresAt === null) return true;
  return Date.now() < accessTokenExpiresAt;
}

/**
 * Terminal auth failure handler. Clears every token layer and bounces to the
 * welcome screen — but ONLY when the session is genuinely unrecoverable.
 *
 * CRITICAL invariant (users must never be logged out unless they sign out
 * themselves): a dead / rotated / raced REFRESH token must NOT log the user
 * out while the ACCESS token still authenticates calls. The access token is
 * the live, year-long credential; the refresh token is only needed to mint a
 * NEW access token near expiry. A WS-reconnect or proactive refresh that 401s
 * on a stale refresh token is therefore non-fatal — we keep the session and
 * keep using the working access token. This was the "random logout despite a
 * valid, cached session" bug.
 *
 * Teardown proceeds only when:
 *   - the access token is itself unusable (absent or past expiry), OR
 *   - `force` is set — the SERVER explicitly revoked this device
 *     (X-Ping-Reauth-Required), which is a genuine remote sign-out.
 *
 * Idempotent via `terminalAuthHandled`.
 */
export async function handleTerminalAuthFailure(
  reason: string,
  opts?: { readonly force?: boolean },
): Promise<void> {
  if (terminalAuthHandled) return;
  if (opts?.force !== true && accessTokenIsUsable()) {
    // Refresh failed but the access token still works → keep the session.
    log.info({ event: 'auth.terminalFailure.suppressed', reason });
    return;
  }
  terminalAuthHandled = true;
  log.info({ event: 'auth.terminalFailure.teardown', reason });
  await clearTokens();
  reauthCallback?.();
}

// Refresh the access token this many ms BEFORE it actually expires, so an
// in-flight request never races the expiry boundary.
const PROACTIVE_REFRESH_SKEW_MS = 30_000;

export function setAccessToken(token: string, expiresInSeconds?: number): void {
  inMemoryAccessToken = token;
  accessTokenExpiresAt =
    expiresInSeconds !== undefined ? Date.now() + expiresInSeconds * 1000 : null;
  // Propagate the bearer to the shared `@ping/ui` network module. That
  // layer has its own token slot (separate from this in-memory one) and is
  // used by features like recovery-blob upload (`POST /v1/recovery/blobs`)
  // and device linking (`POST /v1/linking/tickets`). Without this, those
  // requests went out with no Authorization header and 401'd — linking in
  // particular opts out of the reactive-refresh retry, so it failed outright.
  setSharedApiAuthToken(token);
  // A fresh access token means we have a live session again — re-arm the
  // terminal-failure guard so a future genuine revocation tears down once
  // more (otherwise a sign-out → sign-in within one page load couldn't
  // detect a second revocation).
  terminalAuthHandled = false;
  // Persist the (year-long) access token so a reload can reuse it instead
  // of burning the single-use refresh token. Fire-and-forget; encrypted
  // with the same AES-GCM wrap key as the refresh token.
  void storeWrappedAccessToken(token).catch(() => {
    /* best-effort — falls back to refresh on next reload */
  });
}

export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

/**
 * Clear ONLY the in-memory access token (and its expiry). Does NOT touch
 * the persisted refresh token or identity — used on transient teardown
 * paths where the session must survive a reload. Use `clearTokens()` to
 * also drop the refresh token + identity on a genuine sign-out.
 */
export function clearAccessToken(): void {
  inMemoryAccessToken = null;
  accessTokenExpiresAt = null;
  lastRefreshResult = null;
  lastRefreshAtMs = 0;
  setSharedApiAuthToken(null);
  void clearStoredAccessToken().catch(() => {});
}

/** True when we have a known expiry and it's within the skew window (or
 *  already past). Returns false when expiry is unknown so we never refresh
 *  blindly. */
function accessTokenNeedsRefresh(): boolean {
  if (accessTokenExpiresAt === null) return false;
  return Date.now() >= accessTokenExpiresAt - PROACTIVE_REFRESH_SKEW_MS;
}

/**
 * A genuine "this refresh token is no longer valid" signal from the BE —
 * the ONLY condition under which we should tear down the session. A
 * network error, timeout, or 5xx is transient and must NOT log the user
 * out (their refresh token is still good; the next attempt can succeed).
 */
export function isInvalidTokenError(err: unknown): boolean {
  if (err instanceof NetworkError) {
    // The server's `RequireBackedUp` middleware 403s EVERY device-JWT route —
    // INCLUDING `/v1/auth/refresh` — with `{"error":"backup_required"}` until
    // the post-signup recovery backup completes (services/api/internal/auth/
    // service.go; iOS mirrors this in APIClient). That is an authorization
    // GATE, not a dead token: the refresh token is perfectly valid. Treating it
    // as terminal tore the session down → `forceCleanReload` → re-bootstrap →
    // refresh 403 again → reload, i.e. the "reloads in a loop right after
    // register" bug. Never terminal — the recovery-backup gate (App.tsx) shows
    // the setup flow instead, and a real revocation still arrives as
    // `device_revoked` / the `X-Ping-Reauth-Required` header.
    if (err.statusCode === 403 && err.serverError === 'backup_required') {
      return false;
    }
    // ONLY 401/403 mean "this token is genuinely dead" (the BE returns 401
    // `invalid_refresh_token` / 403 `device_revoked`). A 400 is a
    // malformed-request / transient server condition (`ErrInvalidRequest`),
    // NOT proof the refresh token is invalid — treating it as terminal wiped
    // credentials and bounced the user to welcome on a recoverable blip.
    return err.statusCode === 401 || err.statusCode === 403;
  }
  return false;
}

/**
 * Proactively mint a fresh access token if the current one is within the
 * expiry skew window. No-op when expiry is unknown or the token is still
 * comfortably valid. Swallows transient refresh failures (the reactive
 * 401 path remains the backstop). Re-throws genuine invalid-token errors
 * so callers can tear down.
 */
export async function ensureFreshAccessToken(): Promise<void> {
  if (!accessTokenNeedsRefresh()) return;
  try {
    await performRefresh();
  } catch (err) {
    if (isInvalidTokenError(err)) throw err;
    // Transient — leave the (soon-to-expire) token in place; the reactive
    // 401 retry will cover it.
  }
}

export async function performRefresh(): Promise<RefreshResponse> {
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }

  // Reuse a very recent successful refresh rather than re-POSTing the
  // (single-use, rotated) refresh token — see `lastRefreshResult`. The
  // minted access token is still valid for minutes, so reusing it for a
  // few seconds is safe and prevents the double-consume → 401 → logout.
  if (
    lastRefreshResult !== null &&
    inMemoryAccessToken !== null &&
    Date.now() - lastRefreshAtMs < REFRESH_REUSE_WINDOW_MS
  ) {
    return lastRefreshResult;
  }

  try {
    inFlightRefresh = (async () => {
      const refreshToken = await retrieveRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const result = await refreshAccessToken({ refreshToken });

      // Only overwrite the persisted refresh token when the BE actually
      // ROTATED it (returned a NEW, non-empty refresh_token). Many BEs
      // are non-rotating and return only a fresh access token — in that
      // case `result.refreshToken` is undefined/empty, and clobbering the
      // stored token would 401 the NEXT reload (the exact "logged out
      // after several reloads" symptom). Keep the existing token then.
      const rotated = result.refreshToken;
      const didRotate =
        typeof rotated === 'string' && rotated.length > 0 && rotated !== refreshToken;
      if (didRotate) {
        await storeWrappedRefreshToken(rotated);
      }
      setAccessToken(result.accessToken, result.expiresIn);
      const effective: RefreshResponse = {
        accessToken: result.accessToken,
        refreshToken: didRotate ? rotated : refreshToken,
        ...(result.expiresIn !== undefined ? { expiresIn: result.expiresIn } : {}),
      };
      lastRefreshResult = effective;
      lastRefreshAtMs = Date.now();

      return effective;
    })();

    return await inFlightRefresh;
  } catch (err) {
    // A genuine invalid-token rejection (BE returned 401/403 for the
    // refresh token itself) is TERMINAL: the refresh token is dead, so no
    // amount of retrying will recover it. Tear down centrally here — this
    // is the single choke point every caller funnels through, including
    // the WS reconnect loops that would otherwise hammer `performRefresh`
    // on every backoff tick and never log the user out. Transient errors
    // (network blip, 5xx, timeout) fall through untouched and re-throw so
    // the caller can retry. Idempotent via `handleTerminalAuthFailure`.
    if (isInvalidTokenError(err)) {
      void handleTerminalAuthFailure('performRefresh.invalidToken');
    }
    throw err;
  } finally {
    inFlightRefresh = null;
  }
}

export async function clearTokens(): Promise<void> {
  inMemoryAccessToken = null;
  accessTokenExpiresAt = null;
  lastRefreshResult = null;
  lastRefreshAtMs = 0;
  setSharedApiAuthToken(null);
  // Identity is dropped alongside the refresh token so a subsequent
  // silent-reauth attempt cleanly falls through to the welcome
  // screen instead of seeing orphaned identity material.
  await Promise.all([clearRefreshToken(), clearStoredAccessToken(), clearIdentity()]);
}

/** Read a request header regardless of whether `init.headers` is a plain
 *  object, a `Headers` instance, or an array of tuples. */
function readHeader(init: RequestInit | undefined, name: string): string | null {
  const headers = init?.headers;
  if (headers == null) return null;
  if (headers instanceof Headers) return headers.get(name);
  const lower = name.toLowerCase();
  if (Array.isArray(headers)) {
    const hit = headers.find(([k]) => k.toLowerCase() === lower);
    return hit ? hit[1] : null;
  }
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}

/**
 * True when `url` targets the app's own API origin, so it's safe + correct to
 * attach the bearer. Relative URLs are same-origin (the API in dev, where
 * `VITE_API_BASE_URL` is empty). Absolute URLs must match the configured API
 * origin (or the page origin) — anything else (CDNs, S3, LiveKit, third parties)
 * must NOT receive the token.
 */
function isApiOrigin(url: string): boolean {
  // Relative path → same-origin → our API (dev) or reverse-proxied API (prod).
  if (!/^[a-z]+:\/\//i.test(url)) return true;
  try {
    const reqOrigin = new URL(url).origin;
    const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
    const apiOrigin = base !== '' ? new URL(base).origin : pageOrigin;
    return reqOrigin === apiOrigin || reqOrigin === pageOrigin;
  } catch {
    return false;
  }
}

export async function installRefreshInterceptor(onReauthRequired: () => void): Promise<void> {
  // Publish the callback at module scope so non-fetch paths (the WS
  // reconnect loops via `performRefresh` → `handleTerminalAuthFailure`)
  // can drive the same terminal teardown.
  reauthCallback = onReauthRequired;
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Proactive refresh: if this is an authed request and the access
    // token is about to expire, mint a fresh one BEFORE firing, then
    // rewrite the Authorization header so the request carries the new
    // token. Skipped for the auth endpoints themselves (they must never
    // trigger a nested refresh) and for requests with no Authorization.
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const isAuthEndpoint = url.includes('/v1/auth/');
    const hadAuthHeader = readHeader(init, 'Authorization') !== null;
    // Only ever attach / rewrite the app's bearer for requests to our OWN API
    // origin. Without this guard the token was injected into every cross-origin
    // fetch — including third-party CDNs (e.g. the MediaPipe WASM for call
    // background effects on jsdelivr), which (a) leaks the access token to those
    // hosts and (b) forces a CORS preflight that the CDN rejects ("Request
    // header field authorization is not allowed"), breaking the fetch entirely.
    const eligibleForAuth = isApiOrigin(url);
    if (!isAuthEndpoint && eligibleForAuth && hadAuthHeader && accessTokenNeedsRefresh()) {
      try {
        await ensureFreshAccessToken();
        init = {
          ...init,
          headers: { ...init?.headers, Authorization: `Bearer ${getAccessToken()}` },
        };
      } catch {
        // Genuine invalid-token surfaces as a 401 below and is handled
        // by the reactive path; transient errors are swallowed inside
        // ensureFreshAccessToken.
      }
    } else if (!isAuthEndpoint && eligibleForAuth && !hadAuthHeader) {
      // No Authorization was attached because the in-memory access token isn't
      // populated yet — common right after a linked/ephemeral bootstrap, before
      // the first refresh runs. If a session exists, mint a token now and attach
      // it so authed reads (user search, profile, member hydration) don't 401
      // and silently fail (they're decorative + opt out of reactive reauth, so
      // they'd never retry). `ensureFreshAccessToken` is a no-op when there's no
      // refresh token, so genuinely-unauthenticated requests are unaffected.
      try {
        await ensureFreshAccessToken();
        const token = getAccessToken();
        if (token !== null && token.length > 0) {
          init = {
            ...init,
            headers: { ...init?.headers, Authorization: `Bearer ${token}` },
          };
        }
      } catch {
        // No session / transient — request proceeds and 401s if it needed auth.
      }
    }

    const response = await originalFetch(input, init);

    // Background maintenance calls (e.g. key-package replenish) opt out of
    // the global reauth/logout on 401: they're fire-and-forget and must
    // never tear down the session. A genuine session expiry is still caught
    // by the next foreground call (sync/send). Without this, a replenish
    // that races a mid-bootstrap token wipe would log the user out.
    // NEVER run the reactive refresh/retry on the auth endpoints
    // themselves. `/v1/auth/refresh` returning 401 means the refresh
    // token is bad — retrying it here calls `performRefresh()` again,
    // which (via the in-flight dedupe) awaits the very promise we're
    // inside, DEADLOCKING until the 30s timeout. The caller
    // (`refreshAccessToken` → `fetchJson`) must receive the 401 and
    // throw a real NetworkError so the silent-reauth path can classify
    // it. This was the cause of "restart dev server → logged out".
    if (response.status === 401 && isAuthEndpoint) {
      return response;
    }

    const skipReauth = readHeader(init, 'X-Ping-Skip-Reauth') === '1';
    if (response.status === 401 && skipReauth) {
      return response;
    }

    // Explicit terminal signal from the BE: the device's credentials are
    // genuinely dead (refresh token revoked/rotated-away, device revoked,
    // or account suspended). The BE stamps `X-Ping-Reauth-Required: true`
    // on these responses. Honour it on BOTH 401 and 403 — the
    // `device_revoked` 403 on `/v1/messages` is the FIRST domino in the
    // reload storm, so catching it here tears the session down immediately
    // rather than waiting for a WS-reconnect refresh to 401 and looping.
    // Still respect `skipReauth` (fire-and-forget maintenance calls like
    // key-package replenish must never force a logout) and require an
    // in-memory token (no token ⇒ auth not established yet; see below).
    const reauthRequired = response.headers.get('X-Ping-Reauth-Required') === 'true';
    if (
      (response.status === 401 || response.status === 403) &&
      reauthRequired &&
      !skipReauth &&
      inMemoryAccessToken !== null
    ) {
      // Explicit server revocation (e.g. "sign out all devices" from another
      // device) — force teardown even though our access token may still look
      // locally-valid. This is the ONE non-user-initiated logout we honour.
      await handleTerminalAuthFailure('interceptor.reauthRequiredHeader', { force: true });
      return response;
    }

    // Pre-bootstrap / post-teardown guard. If there is NO in-memory access
    // token, the session hasn't been established yet (the App-level silent
    // reauth is still resolving the persisted refresh token) or was just
    // torn down. A 401 in this window is NOT a mid-session expiry — it's a
    // request that fired before auth was ready (e.g. a directory lookup on
    // a freshly-mounted chat room). Reactively refreshing here would race
    // the App-level silent reauth for the SINGLE-USE refresh token, and on
    // failure `clearTokens()` would DESTROY a still-valid refresh token —
    // the exact "reload → logged out" regression. Surface the 401 and let
    // the App-level reauth path own (re)authentication; it preserves
    // credentials on transient failure so the next attempt succeeds.
    if (response.status === 401 && inMemoryAccessToken === null) {
      return response;
    }

    if (response.status === 401) {
      try {
        await performRefresh();

        const retryInit: RequestInit = {
          ...init,
          headers: {
            ...init?.headers,
            Authorization: `Bearer ${getAccessToken()}`,
          },
        };

        return originalFetch(input, retryInit);
      } catch (err) {
        // Only tear down the session when the refresh token itself is
        // genuinely invalid (BE rejected it with 400/401/403). A
        // transient failure (network blip, timeout, 5xx) must NOT log
        // the user out — their refresh token is still good, so we leave
        // the session intact and let the next foreground call retry.
        // `performRefresh` already fires the terminal teardown on an
        // invalid token; this is the idempotent backstop for the retry
        // path so the caller still gets its original response back.
        if (isInvalidTokenError(err)) {
          await handleTerminalAuthFailure('interceptor.reactiveRefreshInvalid');
        }
        return response;
      }
    }

    return response;
  };
}
